import type { ReactNode } from 'react';
import { categoryColor } from '@/domain/category';
import { useAppState } from '@/ui/AppState';
import type { FlagKind } from '@/domain/types';
import type { ImportIssue } from '@/import/schema';

/**
 * Every string that came from an import is rendered as a text child. Nothing
 * here ever uses dangerouslySetInnerHTML, and a test asserts that.
 */

export function CategoryChip({ category }: { category: string }) {
  const { theme } = useAppState();
  return (
    <span
      className="chip chip--category"
      style={{ ['--chip-color' as string]: categoryColor(category, theme) }}
      title={category}
    >
      {category}
    </span>
  );
}

export function SessionChip({ label }: { label: string }) {
  return <span className="chip chip--session">{label}</span>;
}

export function CountChip({ children }: { children: ReactNode }) {
  return <span className="chip chip--count">{children}</span>;
}

const FLAG_LABELS: Record<FlagKind, { title: string; icon: string }> = {
  filled: { title: 'Filled in', icon: '✎' },
  check: { title: 'Check this', icon: '!' },
  priority: { title: "You marked this 'know this'", icon: '★' },
};

export function FlagBanner({ flag, text }: { flag: FlagKind; text?: string }) {
  const meta = FLAG_LABELS[flag];
  return (
    <div className={`flag flag--${flag}`} data-testid={`flag-${flag}`}>
      <div className="flag__title">
        <span aria-hidden="true">{meta.icon}</span>
        {meta.title}
      </div>
      {text ? <div className="flag__text">{text}</div> : null}
    </div>
  );
}

export function Banner({
  kind,
  children,
}: {
  kind: 'error' | 'warn' | 'ok' | 'info';
  children: ReactNode;
}) {
  return (
    <div className={`banner banner--${kind}`} role={kind === 'error' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

export function IssueList({ issues, limit = 40 }: { issues: ImportIssue[]; limit?: number }) {
  const shown = issues.slice(0, limit);
  return (
    <>
      <ul className="issue-list">
        {shown.map((issue, i) => (
          <li key={`${issue.path}-${i}`}>
            <code>{issue.path}</code> {issue.message}
          </li>
        ))}
      </ul>
      {issues.length > shown.length ? (
        <p className="faint">…and {issues.length - shown.length} more.</p>
      ) : null}
    </>
  );
}

export function EmptyState({
  icon,
  title,
  children,
}: {
  icon: string;
  title: string;
  children?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__icon" aria-hidden="true">
        {icon}
      </span>
      <h2>{title}</h2>
      {children ? <p className="muted">{children}</p> : null}
    </div>
  );
}

export function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        <div className="stack" style={{ marginTop: 'var(--sp-4)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
